import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useParams } from 'react-router-dom';
import { Col, Row } from 'reactstrap';

import { ContainerHeader, ErrorResponse, Spinner } from '~components';

import { Form as WizardForm } from '~components/wizard-form';

import { SupportAtHomeFundingTypes } from '~constants/maps';

import { getAll as getUsers } from '~care-workers/actions';

import { getAll as getClientPackages } from '~main/actions/clients/clientPackages';
import { getClientPackageSchedule } from '~main/actions/clients/clientPackageSchedules';
import {
  selectClientPackages,
  selectLookupSahQuarterlyClientPackages,
} from '~main/selectors/clients/clientPackages';

import steps from '~support-at-home/forms/level-move/new';

interface LevelMoveFormValues {
  client_name: string;
  current_package_name: string;
  schedule_start_date: string;
  schedule_end_date: string;
  schedule_locked_date: string;
  schedule_balance: string;
  new_package_id: number | '';
  changeover_date: string;
  case_manager_id: number | '';
  repeat_appointment_ids: number[];
  fixed_appointment_ids: number[];
  _locked_date: string;
  _end_date: string;
  step2?: unknown;
  step3?: unknown;
}

const formatBalance = (balance: number | null | undefined): string => {
  const value = balance ?? 0;
  return value.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
};

const LevelMove: React.FC = () => {
  const { scheduleId: scheduleIdRaw, clientId: clientIdRaw } = useParams<{ clientId: string; scheduleId: string }>();
  const scheduleId = Number(scheduleIdRaw);
  const clientId = Number(clientIdRaw);
  const dispatch = useAppDispatch();

  const sourceSchedule = useAppSelector(({ main }) =>
    main.clientPackageSchedules.row?.id === scheduleId ? main.clientPackageSchedules.row : undefined,
  );
  const scheduleLoading = useAppSelector(({ main }) => main.clientPackageSchedules.loading);
  const scheduleError = useAppSelector(({ main }) => main.clientPackageSchedules.error);
  const packages = useAppSelector(selectClientPackages);
  const sahQuarterlyPackages = useAppSelector(selectLookupSahQuarterlyClientPackages);
  const packagesLoading = useAppSelector(({ main }) => main.clientPackages.loading);
  const packagesError = useAppSelector(({ main }) => main.clientPackages.error);

  useEffect(() => {
    if (scheduleId) {
      dispatch(getClientPackageSchedule({ id: String(scheduleId) }));
    }
    dispatch(getClientPackages());
    dispatch(getUsers());
  }, [scheduleId]);

  const renderShell = (body: React.ReactNode) => (
    <div className="p-2 level-move">
      <ContainerHeader className="info" dataCy="level-move-header">
        SAH Package Schedule Level Move
      </ContainerHeader>
      {body}
    </div>
  );

  // Still fetching either dependency — show spinner only.
  if (
    scheduleLoading === 'pending' ||
    scheduleLoading === 'idle' ||
    packagesLoading === 'pending' ||
    packagesLoading === 'idle'
  ) {
    return (
      <div className="p-2 level-move">
        <Spinner loading />
      </div>
    );
  }

  // Terminal failure on schedule fetch.
  if (scheduleError || !sourceSchedule) {
    return renderShell(<ErrorResponse message="Unable to load schedule details." />);
  }

  // Terminal failure on packages fetch.
  if (packagesError) {
    return renderShell(<ErrorResponse message="Unable to load packages." />);
  }

  if (sourceSchedule.client_id !== clientId) {
    return renderShell(<ErrorResponse message="Schedule not found for this client." />);
  }

  // Web-side UX guard: this wizard is only meaningful for SAH Quarterly Client
  // schedules. The API enforces this server-side; the check here exists so a
  // user who lands on the URL with a non-SAH schedule sees a clear message
  // rather than a confusing 400 on Step 3.
  if (sourceSchedule.package?.funding_type?.sah_code !== SupportAtHomeFundingTypes.SAH_QUARTERLY_CLIENT) {
    return renderShell(
      <ErrorResponse message="SAH Level Move is only available for SAH Quarterly Client schedules." />,
    );
  }

  // Loaded, but the company has no SAH Quarterly Client packages to move into.
  if (sahQuarterlyPackages.length === 0) {
    return renderShell(
      <ErrorResponse message="No SAH Quarterly Client packages are available to move this schedule into." />,
    );
  }

  const currentPackage = packages.find((pkg) => pkg.id === sourceSchedule.package_id);
  const initialValues: LevelMoveFormValues = {
    client_name: sourceSchedule.client?.full_name ?? '',
    current_package_name: currentPackage?.name ?? '',
    schedule_start_date: sourceSchedule.start_date ?? '',
    schedule_end_date: sourceSchedule.end_date ?? '',
    schedule_locked_date: sourceSchedule.locked_date ?? '',
    schedule_balance: formatBalance(sourceSchedule.balance),
    new_package_id: '',
    changeover_date: '',
    case_manager_id: sourceSchedule.case_manager_id ?? '',
    repeat_appointment_ids: [],
    fixed_appointment_ids: [],
    _locked_date: sourceSchedule.locked_date ?? '',
    _end_date: sourceSchedule.end_date ?? '',
  };

  return (
    <div className="p-2 level-move">
      <ContainerHeader className="info" dataCy="level-move-header">
        SAH Package Schedule Level Move
      </ContainerHeader>
      <Row>
        <Col md={8} className="mx-auto">
          <WizardForm<LevelMoveFormValues> steps={steps} initialValues={initialValues} />
        </Col>
      </Row>
    </div>
  );
};

export default LevelMove;
